
import React, { useState } from 'react';
import { Tag, Plus, X, Calendar, Users, Percent, DollarSign, Trash2, Power, Info, Sparkles, Layers, UserCheck, ChevronDown, Check } from 'lucide-react';
import { Discount, Language } from '../types';
import { formatRiel, SERVICE_CATEGORIES, generateUUID } from '../constants';
// Corrected import path for useTranslation
import { useTranslation } from '../LanguageContext';

interface ShopDiscountProps {
  discounts: Discount[];
  shopId: string;
  onAdd: (discount: Discount) => void;
  onDelete: (id: string) => void;
  onToggleStatus: (id: string) => void;
}

export const ShopDiscount: React.FC<ShopDiscountProps> = ({ discounts, shopId, onAdd, onDelete, onToggleStatus }) => {
  const { language } = useTranslation();
  const [isAdding, setIsAdding] = useState(false);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<'SIMPLE' | 'GROUP' | 'PERIOD' | 'CATEGORY' | 'LOYALTY'>('SIMPLE');
  const [valueType, setValueType] = useState<'PERCENTAGE' | 'FIXED'>('PERCENTAGE');
  const [value, setValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [minGroupSize, setMinGroupSize] = useState('2');
  const [targetCategories, setTargetCategories] = useState<string[]>([]);
  const [minBookings, setMinBookings] = useState('3');

  const texts: Record<string, Record<Language, string>> = {
    title: { EN: 'Campaign Manager', KH: 'គ្រប់គ្រងការផ្តល់ជូនពិសេស', ZH: '活动管理', JA: 'キャンペーン管理', KO: '캠페인 관리' },
    sub: { EN: 'Drive more repairs with seasonal offers.', KH: 'បង្កើនការជួសជុលជាមួយការផ្តល់ជូនតាមរដូវកាល។', ZH: '通过季节性优惠推动更多维修。', JA: '季節ごとのオファーで修理依頼を増やしましょう。', KO: '계절별 혜택으로 수리 요청을 늘리십시오.' },
    add_deal: { EN: 'Launch New Deal', KH: 'បង្កើតការផ្តល់ជូនថ្មី', ZH: '发布新优惠', JA: '新しい特典を開始', KO: '새 혜택 출시' },
    reduction: { EN: 'Reduction', KH: 'ការបញ្ចុះតម្លៃ', ZH: '折扣力度', JA: '割引', KO: '할인' },
    status_active: { EN: 'Active', KH: 'សកម្ម', ZH: '有效', JA: '有効', KO: '활성' },
    status_inactive: { EN: 'Inactive', KH: 'អសកម្ម', ZH: '无效', JA: '無効', KO: '비활성' },
    deploy: { EN: 'Deploy Campaign', KH: 'ដាក់ឱ្យប្រើប្រាស់', ZH: '部署活动', JA: 'キャンペーンを適用', KO: '캠페인 배포' },
    name: { EN: 'Campaign Identity', KH: 'ឈ្មោះការផ្តល់ជូន', ZH: '活动标识', JA: 'キャンペーン名', KO: '캠페인 이름' },
    type: { EN: 'Deal Type', KH: 'ប្រភេទការផ្តល់ជូន', ZH: '优惠类型', JA: '特典タイプ', KO: '혜택 유형' },
    model: { EN: 'Value Model', KH: 'ទម្រង់តម្លៃ', ZH: '价值模型', JA: 'バリューモデル', KO: '가치 모델' },
    amount: { EN: 'Discount Amount', KH: 'ចំនួនបញ្ចុះតម្លៃ', ZH: '折扣金额', JA: '割引額', KO: '할인 금액' },
    desc: { EN: 'Strategic Description', KH: 'ការរៀបរាប់លម្អិត', ZH: '战略描述', JA: '戦略的説明', KO: '전략적 설명' },
    simple: { EN: 'Standard Discount', KH: 'បញ្ចុះតម្លៃទូទៅ', ZH: '标准折扣', JA: '標準割引', KO: '일반 할인' },
    group: { EN: 'Group Sale (Bulk)', KH: 'លក់ជាក្រុម (ច្រើន)', ZH: '拼团优惠', JA: '拼团销售', KO: '그룹 판매' },
    period: { EN: 'Seasonal (Period)', KH: 'តាមរដូវកាល (រយៈពេល)', ZH: '季节性/周期性', JA: '期間限定', KO: '기간 한정' },
    category: { EN: 'Category Group', KH: 'តាមក្រុមប្រភេទ', ZH: '类别组优惠', JA: 'カテゴリ別', KO: '카테고리별' },
    loyalty: { EN: 'Loyalty (Repeat)', KH: 'ភក្ដីភាព (អតិថិជនចាស់)', ZH: '忠诚度奖励', JA: 'ロイヤリティ', KO: '충성도 보상' },
    percentage: { EN: 'Percentage', KH: 'ភាគរយ', ZH: '百分比', JA: 'パーセンテージ', KO: '백분율' },
    fixed: { EN: 'Fixed Riel', KH: 'ចំនួនប្រាក់រៀលថេរ', ZH: '固定瑞尔', JA: 'リエル固定', KO: '리엘 고정' },
    confirm_del: { EN: 'Permanently delete this campaign?', KH: 'តើអ្នកចង់លុបយុទ្ធនាការនេះជាអចិន្ត្រៃយ៍មែនទេ?', ZH: '永久删除此活动？', JA: 'このキャンペーンを完全に削除しますか？', KO: '이 캠페인을 영구적으로 삭제하시겠습니까?' }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const newDiscount: Discount = {
      id: generateUUID(),
      shopId: shopId,
      name,
      description,
      type,
      valueType,
      value: Number(value),
      status: 'ACTIVE',
      ...(type === 'PERIOD' ? { startDate, endDate } : {}),
      ...(type === 'GROUP' ? { minGroupSize: Number(minGroupSize) } : {}),
      ...(type === 'CATEGORY' ? { targetCategories } : {}),
      ...(type === 'LOYALTY' ? { minBookings: Number(minBookings) } : {})
    };
    onAdd(newDiscount);
    resetForm();
  };

  const confirmRemove = (id: string) => {
    if (window.confirm(texts.confirm_del[language])) {
      onDelete(id);
    }
  };

  const resetForm = () => {
    setName('');
    setDescription('');
    setType('SIMPLE');
    setValueType('PERCENTAGE');
    setValue('');
    setStartDate('');
    setEndDate('');
    setMinGroupSize('2');
    setTargetCategories([]);
    setMinBookings('3');
    setIsAdding(false);
  };

  return (
    <div className="space-y-10 animate-in slide-in-from-bottom-4 duration-500">
      <div className="bg-gradient-to-br from-rose-500 to-orange-500 rounded-[3rem] p-10 text-white shadow-2xl shadow-rose-200 relative overflow-hidden group">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-[100px] -mr-32 -mt-32"></div>
        <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-8">
           <div className="text-center md:text-left">
              <h2 className="text-3xl font-black mb-3 tracking-tighter">{texts.title[language]}</h2>
              <p className="text-rose-50 font-bold max-w-sm text-lg opacity-80">{texts.sub[language]}</p>
           </div>
           <button onClick={() => setIsAdding(true)} className="bg-white text-rose-600 px-10 py-5 rounded-[2rem] font-black text-xs uppercase tracking-widest shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center gap-3"><Plus size={20} /> {texts.add_deal[language]}</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {discounts.map(d => (
          <div key={d.id} className={`bg-white rounded-[3rem] p-8 border border-slate-100 shadow-sm transition-all duration-500 hover:shadow-xl group relative flex flex-col h-full ${d.status === 'INACTIVE' ? 'opacity-50 grayscale' : ''}`}>
             <div className="flex justify-between items-start mb-6">
                <div className={`p-5 rounded-[1.8rem] bg-rose-50 text-rose-600 border border-current/10 shadow-sm`}><Percent size={24} /></div>
                <div className="flex gap-2">
                   <button onClick={() => onToggleStatus(d.id)} className={`p-3.5 rounded-2xl transition-all ${d.status === 'ACTIVE' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}><Power size={18} /></button>
                   <button onClick={() => confirmRemove(d.id)} className="p-3.5 bg-red-50 text-red-400 rounded-2xl hover:bg-red-100"><Trash2 size={18} /></button>
                </div>
             </div>
             <h3 className="text-xl font-black text-slate-900 mb-2 truncate tracking-tight">{d.name}</h3>
             <p className="text-xs font-bold text-slate-400 leading-relaxed mb-8 flex-grow">{d.description}</p>
             <div className="space-y-4 pt-6 border-t border-slate-50">
                <div className="flex justify-between items-center"><span className="text-[10px] font-black text-slate-300 uppercase tracking-widest">{texts.reduction[language]}</span><span className="text-3xl font-black text-rose-600 tracking-tighter">{d.valueType === 'PERCENTAGE' ? `${d.value}%` : formatRiel(d.value)}</span></div>
             </div>
          </div>
        ))}
      </div>

      {isAdding && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
           <div className="bg-white rounded-[3.5rem] w-full max-w-xl shadow-2xl overflow-hidden border border-white/20">
              <div className="p-8 border-b border-slate-50 flex justify-between items-center bg-white sticky top-0 z-10">
                 <h3 className="text-2xl font-black text-slate-900 tracking-tighter">Campaign Designer</h3>
                 <button onClick={resetForm} className="w-12 h-12 rounded-2xl bg-slate-50 flex items-center justify-center text-slate-400 hover:text-slate-900"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="p-10 space-y-8 max-h-[75vh] overflow-y-auto no-scrollbar">
                 <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.name[language]}</label><input type="text" required value={name} onChange={e => setName(e.target.value)} className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none" /></div>
                 <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.type[language]}</label>
                       <select value={type} onChange={e => setType(e.target.value as any)} className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none uppercase text-[10px] tracking-widest appearance-none">
                          <option value="SIMPLE">{texts.simple[language]}</option><option value="GROUP">{texts.group[language]}</option><option value="PERIOD">{texts.period[language]}</option><option value="CATEGORY">{texts.category[language]}</option><option value="LOYALTY">{texts.loyalty[language]}</option>
                       </select>
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.model[language]}</label>
                       <div className="flex bg-slate-50 p-1.5 rounded-2xl">
                          <button type="button" onClick={() => setValueType('PERCENTAGE')} className={`flex-1 py-3.5 rounded-xl text-[10px] font-black uppercase ${valueType === 'PERCENTAGE' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400'}`}>{texts.percentage[language]}</button>
                          <button type="button" onClick={() => setValueType('FIXED')} className={`flex-1 py-3.5 rounded-xl text-[10px] font-black uppercase ${valueType === 'FIXED' ? 'bg-white shadow-md text-slate-900' : 'text-slate-400'}`}>{texts.fixed[language]}</button>
                       </div>
                    </div>
                 </div>
                 <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.amount[language]}</label><input type="number" required value={value} onChange={e => setValue(e.target.value)} className="w-full p-5 rounded-2xl bg-slate-50 font-black text-slate-900 outline-none" /></div>
                 <div className="space-y-2"><label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">{texts.desc[language]}</label><textarea value={description} onChange={e => setDescription(e.target.value)} className="w-full p-6 rounded-[2rem] bg-slate-50 font-medium text-slate-900 outline-none h-28 resize-none" /></div>
                 <button type="submit" className="w-full py-8 bg-slate-900 text-white rounded-[2.5rem] font-black text-xl hover:bg-rose-600 transition-all uppercase tracking-widest flex items-center justify-center gap-4"><Sparkles size={24} fill="currentColor" /> {texts.deploy[language]}</button>
              </form>
           </div>
        </div>
      )}
    </div>
  );
};
